

"use client";

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Phone, KeyRound, Loader2, Eye, EyeOff, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PALETTE } from '../lib/colorPallete';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { useOutsideClick } from '../hooks/useOutsideClick';

const PasswordField = ({ value, onChange, placeholder, name }: { value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, placeholder: string, name: string }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative">
            <input 
                type={isVisible ? "text" : "password"}
                placeholder={placeholder}
                name={name}
                value={value}
                onChange={onChange}
                minLength={6}
                className="w-full p-3 rounded-md bg-stone-100 border border-stone-300 focus:ring-2 focus:ring-amber-500/80 pr-10 text-stone-900 placeholder:text-stone-400"
            />
            <button type="button" onClick={() => setIsVisible(p => !p)} className="absolute top-1/2 right-3 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                {isVisible ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
        </div>
    );
};

const SettingsModal = () => {
    const { user, setUser, isSettingsModalOpen, closeSettingsModal } = useAuth();
    const ref = useOutsideClick<HTMLDivElement>(closeSettingsModal);

    const [formData, setFormData] = useState({
        name: user?.name || '',
        phone: user?.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    if (!user) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (formData.newPassword && formData.newPassword !== formData.confirmNewPassword) {
            setError("New passwords do not match.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.put('/api/user/update-profile', formData);
            if (response.data.success) {
                setUser(response.data.user); // Update user in context
                setSuccess('Profile updated successfully!');
                setTimeout(() => {
                    setSuccess(null);
                    closeSettingsModal();
                }, 2000);
            }
        } catch (err) {
            const axiosError = err as AxiosError<{ message: string }>;
            setError(axiosError.response?.data?.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <AnimatePresence>
            {isSettingsModalOpen && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <motion.div ref={ref} initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-8">
                        <button onClick={closeSettingsModal} className="absolute top-4 right-4 p-1 rounded-full text-stone-400 hover:bg-stone-100 transition-colors z-10"><X size={20} /></button>
                        
                        <h2 className="text-2xl font-bold font-serif text-center" style={{ color: PALETTE.darkGreen }}>Account Settings</h2>
                        <p className="text-center text-stone-500 mb-6 mt-2">Update your profile information.</p>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <fieldset className="space-y-4">
                                <legend className="font-semibold text-stone-700 sr-only">Profile Information</legend>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                                    <input 
                                        type="email" 
                                        value={user.email} 
                                        readOnly 
                                        disabled
                                        className="w-full p-3 pl-10 rounded-md bg-stone-200 border border-stone-300 text-stone-500 cursor-not-allowed"
                                        title="Email cannot be changed."
                                    />
                                </div>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" required className="w-full p-3 pl-10 rounded-md bg-stone-100 border border-stone-300 text-stone-900"/>
                                </div>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" required className="w-full p-3 pl-10 rounded-md bg-stone-100 border border-stone-300 text-stone-900"/>
                                </div>
                                <PasswordField name="currentPassword" value={formData.currentPassword} onChange={handleChange} placeholder="Current Password" />
                            </fieldset>

                            <fieldset className="space-y-4 pt-6 border-t border-stone-200">
                                <legend className="font-semibold text-stone-700 flex items-center gap-2"><KeyRound size={18} /> Change Password (Optional)</legend>
                                <PasswordField name="newPassword" value={formData.newPassword} onChange={handleChange} placeholder="New Password" />
                                <PasswordField name="confirmNewPassword" value={formData.confirmNewPassword} onChange={handleChange} placeholder="Confirm New Password" />
                            </fieldset>

                            {error && <p className="text-center text-sm text-red-500">{error}</p>}
                            {success && <p className="text-center text-sm text-green-600">{success}</p>}

                            <button type="submit" disabled={isLoading} className="w-full py-3 mt-2 rounded-full font-semibold text-white flex items-center justify-center gap-2 disabled:bg-stone-400" style={{ backgroundColor: PALETTE.darkerGreen }}>
                                {isLoading ? <><Loader2 className="animate-spin" size={20} /> Saving Changes...</> : 'Save Changes'}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SettingsModal;